(() => {
  const PRODUCT_SELECTOR = "[data-product-reassurance]";
  let scheduled = false;
  let trailingInitTimer = null;

  function getIntervalMs(el) {
    const raw = parseFloat(el.getAttribute("data-interval") || "4000");
    // supports both "4" (seconds) and "4000" (ms)
    const ms = raw <= 20 ? raw * 1000 : raw;
    return Number.isFinite(ms) ? Math.max(2000, ms) : 4000;
  }

  class ProductReassurance {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.destroyed = false;

      this.track = root.querySelector(".product-reassurance__track");
      this.dots = Array.from(root.querySelectorAll(".product-reassurance__dot"));
      this.originalSlides = Array.from(
        root.querySelectorAll(".product-reassurance__slide:not(.is-reassurance-clone)")
      );
      this.originalCount = this.originalSlides.length;

      if (!this.track || this.originalCount <= 0) return;

      this.index = 0;
      this.timer = null;
      this.snapTimer = null;

      this.interval = getIntervalMs(root);
      // Force the carousel to animate regardless of the user's
      // prefers-reduced-motion setting (brand choice).
      this.reducedMotion = false;
      this.transitionMs = 520;

      this.setupLoop();
      this.bind();
      this.goTo(0, false);

      if (this.originalCount > 1 && !this.reducedMotion) {
        this.start();
      }
    }

    destroy() {
      this.destroyed = true;
      this.stop();
      try {
        this.abort.abort();
      } catch (e) {}
      this.root = null;
      this.track = null;
      this.dots = [];
      this.originalSlides = [];
    }

    setupLoop() {
      if (!this.track || this.originalCount <= 1) return;

      // Clean old clone(s) if any
      this.track.querySelectorAll(".is-reassurance-clone").forEach((n) => n.remove());

      // Clone first slide to end for seamless loop
      const clone = this.originalSlides[0].cloneNode(true);
      clone.classList.add("is-reassurance-clone");
      clone.setAttribute("aria-hidden", "true");
      this.track.appendChild(clone);

      const onEnd = () => {
        if (this.index === this.originalCount) this.snapToFirst();
      };

      this.track.addEventListener(
        "transitionend",
        (e) => {
          if (e.propertyName === "transform") onEnd();
        },
        { signal: this.abort.signal }
      );

      this.track.addEventListener("transitioncancel", onEnd, { signal: this.abort.signal });

      // Handle tab visibility changes
      document.addEventListener(
        "visibilitychange",
        () => {
          if (!document.hidden && this.index === this.originalCount) {
            this.snapToFirst();
          }
        },
        { signal: this.abort.signal }
      );
    }

    bind() {
      // Dot navigation (delegated click handler)
      this.root.addEventListener(
        "click",
        (e) => {
          const dot = e.target.closest(".product-reassurance__dot");
          if (!dot) return;

          const target = parseInt(dot.getAttribute("data-dot"), 10);
          if (!Number.isFinite(target)) return;

          // Handle wrapping from last to first
          if (target === 0 && this.index === this.originalCount - 1) {
            this.goTo(this.originalCount, true); // Go to clone first
          } else {
            this.goTo(target, true);
          }

          this.restart();
        },
        { signal: this.abort.signal }
      );

      // Pause on hover/focus (better UX for product pages)
      const pause = () => this.stop();
      const resume = () => this.start();

      this.root.addEventListener("mouseenter", pause, { signal: this.abort.signal });
      this.root.addEventListener("mouseleave", resume, { signal: this.abort.signal });
      this.root.addEventListener("focusin", pause, { signal: this.abort.signal });
      this.root.addEventListener("focusout", resume, { signal: this.abort.signal });
    }

    goTo(i, animate = true) {
      if (!this.track || this.destroyed) return;

      if (this.snapTimer) {
        clearTimeout(this.snapTimer);
        this.snapTimer = null;
      }

      this.index = i;

      if (!animate || this.reducedMotion) {
        this.track.style.transition = "none";
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        void this.track.offsetHeight; // Force reflow
        this.track.style.transition = "";
      } else {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
      }

      this.updateDots();

      // Schedule snap back to first real slide after animation
      if (animate && this.index === this.originalCount && !this.reducedMotion) {
        this.snapTimer = setTimeout(() => {
          if (!this.destroyed && this.index === this.originalCount) {
            this.snapToFirst();
          }
        }, this.transitionMs);
      }
    }

    snapToFirst() {
      if (!this.track || this.destroyed) return;

      if (this.snapTimer) {
        clearTimeout(this.snapTimer);
        this.snapTimer = null;
      }

      // Instantly snap back to first slide without animation
      this.track.style.transition = "none";
      this.track.style.transform = "translateX(0)";
      void this.track.offsetHeight; // Force reflow
      this.track.style.transition = "";

      this.index = 0;
      this.updateDots();
    }

    updateDots() {
      if (this.originalCount <= 0) return;
      const displayIndex = this.index % this.originalCount;

      this.dots.forEach((d, idx) => {
        const active = idx === displayIndex;
        d.classList.toggle("is-active", active);
        d.setAttribute("aria-pressed", active ? "true" : "false");
      });

      // Update mobile progress bar
      const progress = this.originalCount > 1
        ? (displayIndex / (this.originalCount - 1)) * 100
        : 100;
      this.root.style.setProperty("--reassurance-progress", `${progress}%`);
    }

    next() {
      if (this.originalCount <= 1 || this.destroyed) return;
      
      if (this.index === this.originalCount - 1) {
        this.goTo(this.originalCount, true); // Go to clone
      } else {
        this.goTo(this.index + 1, true);
      }
    }

    start() {
      if (this.destroyed || this.originalCount <= 1 || this.timer || this.reducedMotion) {
        return;
      }

      const tick = () => {
        this.timer = null;
        if (this.destroyed) return;

        // If element got detached from DOM, stop
        if (!this.root || !this.root.isConnected) return;

        this.next();
        this.start();
      };

      this.timer = window.setTimeout(tick, this.interval);
    }

    stop() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.snapTimer) {
        clearTimeout(this.snapTimer);
        this.snapTimer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  function init() {
    document.querySelectorAll(PRODUCT_SELECTOR).forEach((el) => {
      const slidesCount = el.querySelectorAll(
        ".product-reassurance__slide:not(.is-reassurance-clone)"
      ).length;

      if (!slidesCount) return;

      const desiredInterval = getIntervalMs(el);

      // Don't destroy/recreate on every mutation
      // Only rebuild if element was replaced or config changed
      const inst = el.__productReassurance;

      if (
        inst &&
        !inst.destroyed &&
        inst.root === el &&
        el.isConnected &&
        inst.originalCount === slidesCount &&
        inst.interval === desiredInterval
      ) {
        // Still valid → keep instance
        return;
      }

      // Destroy old instance and create new one
      if (inst) inst.destroy();
      el.__productReassurance = new ProductReassurance(el);
    });
  }

  function scheduleInit() {
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        init();
      });
    }

    // Trailing init to catch async updates
    if (trailingInitTimer) clearTimeout(trailingInitTimer);
    trailingInitTimer = setTimeout(init, 350);
  }

  // Initialize on page load
  document.addEventListener("DOMContentLoaded", scheduleInit);
  
  // Reinitialize when sections load (theme editor)
  document.addEventListener("shopify:section:load", scheduleInit);
  
  // Reinitialize on variant changes (if product form updates DOM)
  document.addEventListener("variant:change", scheduleInit);

  // Initial call
  scheduleInit();
})();